import { FunctionComponent, useState } from 'react'
import { useMutation } from '@apollo/client'
import { SelectOptionsDeprecated } from '@/types/Select'
import styles from './cronLine.module.scss'
import { UPDATE_CRON } from '@/modules/API/software/cronHandler'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Day, Hour, initDayTab, initHourTab, initMinuteTab, initMonthTab, Minute, Month } from './initValueTab'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface CronLineProps {
    purpose: string
    minute: string
    hour: string
    day: string
    month: string
}

type cronPropose = {
    [key: string]: string
}

const CronLine: FunctionComponent<CronLineProps> = ({ purpose, minute, hour, day, month }: CronLineProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [minuteValue, setMinute] = useState<string>(minute)
    const [hourValue, setHour] = useState<string>(hour)
    const [dayValue, setDay] = useState<string>(day)
    const [monthValue, setMonth] = useState<string>(month)

    const proposeMapping: cronPropose = {
        annualReport: t('annualReport'),
        monthlyReport: t('monthlyReport'),
        purgeMedia: t('purgeMedia'),
        sendNotificationAccountAfterOneYear: t('sendNotificationAccountAfterOneYear'),
        sendNotificationAccountNotCompleted: t('sendNotificationAccountNotCompleted'),
        sendNotificationParentRecap: t('sendNotificationParentRecap'),
        sendNotificationRemindBook: t('sendNotificationRemindBook'),
        purgeEvents: t('purgeEvents'),
        sendRequestNotification: t('sendRequestNotification'),
        sendNotificationUpdateAccount: t('sendNotificationUpdateAccount'),
        sendNotificationDaycareParentUpdate: t('sendNotificationDaycareParentUpdate'),
        sendAnnuaireVisitKnownDaycare: t('sendAnnuaireVisitKnownDaycare'),
        sendAnnuaireVisitUnknownDaycare: t('sendAnnuaireVisitUnknownDaycare'),
        sendNotificationScheduledSlots: t('sendNotificationScheduledSlots'),
        sendNotificationTaskNotes: t('sendNotificationTaskNotes')
    }

    const title: string = proposeMapping[purpose]

    const [updateCron, { loading: loadingCron }] = useMutation(UPDATE_CRON, {
        variables: {
            purpose,
            minute: minuteValue,
            hour: hourValue,
            day: dayValue,
            month: monthValue
        },
        onCompleted: () => {
            setMinute(minuteValue)
            setHour(hourValue)
            setDay(dayValue)
            setMonth(monthValue)

            showNotificationSuccess(t('cronUpdated'))
        },
        onError: () => {
            showNotificationError(t('errorUpdateCron'))
        }
    })

    const selectMinuteOptions: SelectOptionsDeprecated = [
        {
            options: initMinuteTab().map((minute: Minute) => {
                return {
                    name: minute.minutes,
                    value: minute.id
                }
            })
        }
    ]

    const selectHourOptions: SelectOptionsDeprecated = [
        {
            options: initHourTab().map((hour: Hour) => {
                return {
                    name: hour.hours,
                    value: hour.id
                }
            })
        }
    ]

    const selectDayOptions: SelectOptionsDeprecated = [
        {
            options: initDayTab().map((day: Day) => {
                return {
                    name: day.days,
                    value: day.id
                }
            })
        }
    ]

    const selectMonthOptions: SelectOptionsDeprecated = [
        {
            options: initMonthTab().map((month: Month) => {
                return {
                    name: month.months,
                    value: month.id
                }
            })
        }
    ]

    return (
        <div className={styles.Container}>
            <div className={styles.TextContainer}>{title || purpose}</div>
            <div className={styles.InputContainer}>
                <div className={styles.Input}>
                    <SelectUnique
                        label={t('minutes')}
                        selectValue={minuteValue}
                        setSelectValue={setMinute}
                        selectOptions={selectMinuteOptions}
                    />
                </div>
                <div className={styles.Input}>
                    <SelectUnique
                        label={t('hours')}
                        selectValue={hourValue}
                        setSelectValue={setHour}
                        selectOptions={selectHourOptions}
                    />
                </div>
                <div className={styles.Input}>
                    <SelectUnique
                        label={t('days')}
                        selectValue={dayValue}
                        setSelectValue={setDay}
                        selectOptions={selectDayOptions}
                    />
                </div>
                <div className={styles.Input}>
                    <SelectUnique
                        label={t('months')}
                        selectValue={monthValue}
                        setSelectValue={setMonth}
                        selectOptions={selectMonthOptions}
                    />
                </div>
                <ButtonTextIcon
                    type={'update'}
                    disabled={loadingCron}
                    onClick={() => updateCron()}
                    className={styles.Button}
                />
            </div>
        </div>
    )
}

export default CronLine
